import { AloftDate, SimilarityFeature, dropNaN } from '@aloft/toolkit-shared';

import { CompSelectionProperty } from '../../types.ts';

type ScSimilarityFeatureParams = {
  weight: number /** How important is the feature in the similarity calculation */;
  overrideForSubject?: (effectiveDate: AloftDate) => number /** Value override for subject */;
  maxRange: number /** Maximum range for the feature */;
};

type SimilarityFeatureDefs = Record<SimilarityFeature, ScSimilarityFeatureParams>;
export const similarityFeatureDefs: SimilarityFeatureDefs = {
  gla: {
    weight: 25,
    maxRange: 2500 /** 2,500 sqft */,
  },
  proximityMiles: {
    weight: 45,
    overrideForSubject: () => 0,
    maxRange: 10 /** 10 miles */,
  },
  actualAge: {
    weight: 4,
    maxRange: 35 /** 35 years */,
  },
  quality: {
    weight: 0,
    maxRange: 5 /** quality scale 1–5 */,
  },
  condition: {
    weight: 0,
    maxRange: 5 /** condition scale 1–5 */,
  },
  lotSize: {
    weight: 12,
    maxRange: 2000 /** 2,000 sqft */,
  },
  saleDate: {
    weight: 6,
    overrideForSubject: (effectiveDate) => effectiveDate.toNumericDate(),
    maxRange: 365 /** 1 year in days */,
  },
  bedroomCount: {
    weight: 0,
    maxRange: 4 /** 4 bedrooms */,
  },
  fullBathCount: {
    weight: 6,
    maxRange: 4 /** 4 bathrooms */,
  },
  halfBathCount: {
    weight: 0,
    maxRange: 4 /** 4 half bathrooms */,
  },
  garage: {
    weight: 0,
    maxRange: 1 /** boolean: 0 or 1 */,
  },
  basement: {
    weight: 0,
    maxRange: 1 /** boolean: 0 or 1 */,
  },
  pool: {
    weight: 0,
    maxRange: 1 /** boolean: 0 or 1 */,
  },
};

type SimilarityFeatureMapping = Record<
  SimilarityFeature,
  (comp: CompSelectionProperty) => number | undefined
>;
export const similarityFeatureMapping: SimilarityFeatureMapping = {
  gla: (comp) => dropNaN(comp.gla?.getUnitAmount('sqft') ?? NaN),
  proximityMiles: (comp) => comp.distanceToSubject,
  actualAge: (comp) =>
    comp.yearBuilt !== undefined ? new Date().getFullYear() - comp.yearBuilt : undefined,
  quality: (comp) => comp.quality,
  condition: (comp) => comp.condition,
  lotSize: (comp) => dropNaN(comp.lotSize?.getUnitAmount('sqft') ?? NaN),
  saleDate: (comp) => comp.saleDate?.toNumericDate(),
  bedroomCount: (comp) => comp.bedroomCount,
  fullBathCount: (comp) => comp.fullBaths,
  halfBathCount: (comp) => comp.halfBaths,
  garage: (comp) => (comp.garage ? 1 : 0),
  basement: (comp) => (comp.basement ? 1 : 0),
  pool: (comp) => (comp.pool ? 1 : 0),
};
